import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const POLL_INTERVAL_MS = 15_000;

export type AppointmentNotification = {
  id: string;
  type: 'booked' | 'cancelled';
  appointment_id: string;
  client_name: string | null;
  datetime_start: string | null;
  resource_name: string | null;
  appointment_type_name: string | null;
  timezone?: string;
  read: boolean;
  created_at: number;
};

export const TOAST_VARIANTS = {
  booked: { title: 'Neue Buchung', bg: '#34C759' },
  cancelled: { title: 'Termin storniert', bg: '#FF3B30' },
} as const;

// "Fr 16:00" für die kommende Woche, sonst "Fr 03.10. 16:00"
function formatSlot(iso: string | null, timeZone = 'Europe/Berlin') {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? '';
  const weekday = get('weekday').replace('.', '');
  const time = `${get('hour')}:${get('minute')}`;
  const daysAway = (date.getTime() - Date.now()) / 86_400_000;
  return daysAway > -1 && daysAway < 6
    ? `${weekday} ${time}`
    : `${weekday} ${get('day')}.${get('month')}. ${time}`;
}

export function formatRelative(ms: number) {
  const diffMin = Math.floor((Date.now() - ms) / 60_000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `vor ${diffHours} Std`;
  if (diffHours < 48) return 'Gestern';
  return new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

// Name · Wochentag Uhrzeit · Service · Resource
export function notificationSubline(item: AppointmentNotification) {
  return [
    item.client_name,
    formatSlot(item.datetime_start, item.timezone),
    item.appointment_type_name,
    item.resource_name,
  ].filter(Boolean).join(' · ');
}

// Ein gemeinsamer Poller für Inbox-Glocke und Dashboard-Toasts
export function useAppointmentNotifications() {
  const { user, getAuthHeader } = useAuth();
  const [notifications, setNotifications] = useState<AppointmentNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Zeitpunkt des letzten erfolgreichen Abrufs; null bis zum ersten Erfolg
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/notifications`, { headers: await getAuthHeader() });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread || 0);
      setLoadError(false);
      setFetchedAt(Date.now());
    } catch (err) {
      console.error('[INBOX] Meldungen konnten nicht geladen werden:', err);
      setLoadError(true);
    } finally {
      setLoaded(true);
    }
  }, [user, getAuthHeader]);

  const markRead = useCallback(async (ids?: string[]) => {
    if (ids && !ids.length) return;
    const changed = notifications.filter(n => !n.read && (!ids || ids.includes(n.id))).length;
    // Optimistisch: Badge sofort anpassen, der nächste Poll korrigiert bei Fehlern
    setNotifications(prev => prev.map(n => (!ids || ids.includes(n.id) ? { ...n, read: true } : n)));
    setUnreadCount(count => (ids ? Math.max(0, count - changed) : 0));
    try {
      await axios.post(`${API_BASE_URL}/admin/notifications/read`, ids ? { ids } : {}, { headers: await getAuthHeader() });
    } catch (err) {
      console.error('[INBOX] Als gelesen markieren fehlgeschlagen:', err);
      refresh();
    }
  }, [notifications, getAuthHeader, refresh]);

  // Polling nur bei sichtbarem Tab; beim Zurückkehren sofort aktualisieren
  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const initial = window.setTimeout(poll, 0);
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [refresh]);

  return { notifications, unreadCount, loaded, loadError, fetchedAt, refresh, markRead };
}

export type AppointmentNotificationsState = ReturnType<typeof useAppointmentNotifications>;
